import React, { forwardRef, useCallback, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { css } from 'styled-components';
import { Tooltip } from '@cvent/carina/components/Tooltip';

// SUI only dims a button with the disabled attribute. A control kept focusable for this tooltip uses aria-disabled
// instead, so it takes these styles to look disabled the same way.
export const ariaDisabledStyles = css`
    &[aria-disabled='true'] {
        cursor: not-allowed;
        opacity: 0.5;
    }
`;

const Trigger = forwardRef(({ as: Container, focusTarget, open, close, children, ...containerProps }, ref) => {
    const containerRef = useRef(null);
    const setRef = useCallback(
        (node) => {
            containerRef.current = node;
            // eslint-disable-next-line no-param-reassign
            ref.current = node;
        },
        [ref],
    );

    useEffect(() => {
        const target = focusTarget && containerRef.current?.closest(focusTarget);
        if (!target) {
            return undefined;
        }
        target.addEventListener('focus', open);
        target.addEventListener('blur', close);
        return () => {
            target.removeEventListener('focus', open);
            target.removeEventListener('blur', close);
        };
    }, [focusTarget, open, close]);

    return (
        <Container
            {...containerProps}
            ref={setRef}
            onMouseEnter={open}
            onMouseLeave={close}
            onFocus={open}
            onBlur={close}
        >
            {children}
        </Container>
    );
});

Trigger.propTypes = {
    as: PropTypes.elementType.isRequired,
    focusTarget: PropTypes.string,
    open: PropTypes.func.isRequired,
    close: PropTypes.func.isRequired,
    children: PropTypes.node.isRequired,
};

Trigger.defaultProps = {
    focusTarget: null,
};

/*
 * Shows why a control is disabled, on mouse hover and on keyboard focus. Carina's Tooltip renders the reason hidden;
 * a function child gets its id to set as the control's aria-describedby, so screen readers announce the reason.
 * Keep the control focusable: mark it aria-disabled and leave out its click handler instead of setting `disabled`.
 * When focus lands on an ancestor instead, like the menu item around the content, set `focusTarget` to a selector
 * for it so the tooltip also opens while that ancestor has focus.
 */
const DisabledReasonTooltip = ({ reason, container, focusTarget, children, ...containerProps }) => (
    <Tooltip
        text={reason}
        portal
        trigger={(open, close, ref, describedById) => (
            <Trigger {...containerProps} as={container} focusTarget={focusTarget} open={open} close={close} ref={ref}>
                {typeof children === 'function' ? children(describedById) : children}
            </Trigger>
        )}
    />
);

DisabledReasonTooltip.propTypes = {
    reason: PropTypes.string.isRequired,
    container: PropTypes.elementType,
    focusTarget: PropTypes.string,
    children: PropTypes.oneOfType([PropTypes.func, PropTypes.node]).isRequired,
};

DisabledReasonTooltip.defaultProps = {
    container: 'div',
    focusTarget: null,
};

export default DisabledReasonTooltip;
